<template>
    <div
        class="ds-version-card tw-border tw-border-solid tw-border-border-color tw-bg-white tw-rounded tw-px-4 tw-py-1 lg:tw-px-6 lg:tw-py-3 hover:tw-bg-secondary-light hover:tw-border-slate-500 tw-duration-300"
    >
        <dk-accordion class="ds-version-card__accordion" :has-borders="false" :expanded="isExpanded" no-transition>
            <span
                slot="title"
                class="ds-version-card__header tw-flex tw-flex-col tw-items-start lg:tw-flex-row lg:tw-items-center"
            >
                <span class="ds-version-card__title tw-text-base lg:tw-text-xl tw-leading-base lg:tw-leading-lg">
                    {{ versionTitle }}
                </span>
                <span
                    class="ds-version-card__number lg:tw-ml-2 tw-text-xs lg:tw-text-base tw-text-base-subtle tw-font-normal"
                >
                    {{ versionsNumber }}
                </span>
            </span>
            <hr
                class="ds-version-card__divider -tw-mx-4 lg:-tw-mx-6 tw-mt-1 tw-mb-4 tw-border-t tw-border-solid tw-border-border-color"
            >
            <div class="ds-version-card__details tw-hidden lg:tw-flex">
                <h4
                    class="ds-version-card__specs-item-desk tw-basis-[21.5%] tw-text-base-subtle caption tw-leading-xs tw-uppercase"
                >
                    {{ $t('model.version.version') }}
                </h4>
                <h4
                    class="ds-version-card__specs-item-desk tw-basis-[13%] tw-text-base-subtle caption tw-leading-xs tw-uppercase"
                >
                    {{ $t('model.version.power') }}
                </h4>
                <h4
                    class="ds-version-card__specs-item-desk tw-basis-[13%] tw-text-base-subtle caption tw-leading-xs tw-uppercase"
                >
                    {{ $t('model.version.gearbox') }}
                </h4>
                <h4
                    class="ds-version-card__specs-item-desk tw-basis-[13%] tw-text-base-subtle caption tw-leading-xs tw-uppercase"
                >
                    {{ $t('model.version.traction') }}
                </h4>
                <h4
                    class="ds-version-card__specs-item-desk tw-basis-[13%] tw-text-base-subtle caption tw-leading-xs tw-uppercase"
                >
                    {{ $t('model.version.price') }}
                </h4>
            </div>
            <hr
                class="ds-version-card__divider tw-hidden lg:tw-block -tw-mx-6 tw-mt-4 tw-mb-6 tw-border-t tw-border-solid tw-border-border-color"
            >
            <div
                v-for="(item, index) in version.items"
                :key="item.id"
                class="ds-offer-card__item-container"
                :class="{ 'tw-mb-3': index === version.items.length - 1 }"
            >
                <div class="ds-version-card__item">
                    <div class="ds-version-card__item-container-mobile lg:tw-hidden tw-mb-4">
                        <div class="tw-flex tw-items-stretch tw-justify-between">
                            <div class="ds-version-card__name tw-flex tw-flex-col tw-justify-between tw-text-black">
                                <h3
                                    class="ds-version-card__version-title tw-text-base lg:tw-text-lg tw-leading-base tw-font-semibold"
                                >
                                    {{ item.name }}
                                </h3>
                                <a
                                    :href="item.href"
                                    :title="item.name"
                                    class="ds-version-card__tech-specs-link tw-text-xs lg:tw-text-base tw-underline tw-leading-sm"
                                    variant="subtle"
                                    @click="onSelectVersion(item.id)"
                                >
                                    {{ $t('model.version.tech-specs') }}
                                </a>
                            </div>
                            <div
                                class="ds-version-card__pricing-container tw-flex tw-flex-col tw-justify-between tw-ml-3 tw-shrink-0"
                            >
                                <dk-badge-dst type="light-blue" size="small">
                                    <span class="tw-whitespace-nowrap">{{ displayOffer(item).label }}</span>
                                    <dk-icon name="ds-tag-price-filled" size="xs" class="tw-ml-2" />
                                </dk-badge-dst>
                                <div class="ds-version-card__pricing tw-flex tw-items-center tw-mt-2">
                                    <h4
                                        class="ds-version-card__price-main tw-font-semibold lg:tw-font-bold lg:tw-text-lg tw-text-black tw-leading-base"
                                    >
                                        {{ displayOffer(item).finalPrice || displayOffer(item).originalPrice }}
                                    </h4>
                                    <del
                                        v-if="
                                            displayOffer(item).finalPrice &&
                                                displayOffer(item).originalPrice &&
                                                displayOffer(item).finalPrice !== displayOffer(item).originalPrice
                                        "
                                        class="ds-version-card__price-old"
                                    >
                                        {{ displayOffer(item).originalPrice }}
                                    </del>
                                </div>
                            </div>
                        </div>
                        <div class="ds-version-card__cta tw-mt-3 tw-mb-6">
                            <dk-button
                                class="ds-version-card__cta-button tw-w-full"
                                type="button"
                                @click="onSelect(item.pricing, item.name, item.id)"
                            >
                                {{ $t('model.version.select') }}
                            </dk-button>

                            <dk-button
                                v-if="item.pricing.length > 1"
                                variant="ghost"
                                role="button"
                                class="ds-version-card__offers-link tw-w-full tw-mt-1 tw-text-xs lg:tw-text-base tw-leading-sm"
                                @click="handleOfferLinkClick(item.pricing, item.name, item.id)"
                            >
                                {{ $tc('model.version.other-offers', item.pricing.length, { n: item.pricing.length }) }}
                            </dk-button>
                        </div>
                        <div class="ds-version-card__specs tw-grid tw-text-2xs lg:tw-text-base">
                            <div class="ds-version-card__specs-item tw-flex tw-flex-col">
                                <span class="ds-version-card__specs-label tw-leading-2xs tw-uppercase lg:tw-hidden">
                                    {{ $t('model.version.power') }}
                                </span>
                                <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                    {{ item.power }}
                                </span>
                            </div>
                            <div class="ds-version-card__specs-item tw-flex tw-flex-col">
                                <span class="ds-version-card__specs-label tw-leading-2xs tw-uppercase lg:tw-hidden">
                                    {{ $t('model.version.gearbox') }}
                                </span>
                                <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                    {{ item.gearbox }}
                                </span>
                            </div>
                            <div class="ds-version-card__specs-item tw-flex tw-flex-col">
                                <span class="ds-version-card__specs-label tw-leading-2xs tw-uppercase lg:tw-hidden">
                                    {{ $t('model.version.traction') }}
                                </span>
                                <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                    {{ item.traction }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="ds-version-card__item-container-desk tw-mb-4 lg:tw-mb-6 tw-hidden lg:tw-flex">
                        <div class="ds-version-card__name tw-text-black tw-basis-[21.5%]">
                            <h3
                                class="ds-version-card__version-title tw-text-base lg:tw-text-lg tw-leading-base tw-font-semibold"
                            >
                                {{ item.name }}
                            </h3>
                            <a
                                :href="item.href"
                                :title="item.name"
                                class="ds-version-card__tech-specs-link tw-text-xs lg:tw-text-base tw-underline tw-leading-sm"
                                variant="subtle"
                                @click="onSelectVersion(item.id)"
                            >
                                {{ $t('model.version.tech-specs') }}
                            </a>
                        </div>
                        <div class="ds-version-card__specs-item tw-flex tw-flex-col tw-basis-[13%]">
                            <span class="tw-leading-2xs tw-uppercase lg:tw-hidden">
                                {{ $t('model.version.power') }}
                            </span>
                            <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                {{ item.power }}
                            </span>
                        </div>
                        <div class="ds-version-card__specs-item tw-flex tw-flex-col tw-basis-[13%]">
                            <span class="tw-leading-2xs tw-uppercase lg:tw-hidden">
                                {{ $t('model.version.gearbox') }}
                            </span>
                            <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                {{ item.gearbox }}
                            </span>
                        </div>
                        <div class="ds-version-card__specs-item tw-flex tw-flex-col tw-basis-[13%]">
                            <span class="tw-leading-2xs tw-uppercase lg:tw-hidden">
                                {{ $t('model.version.traction') }}
                            </span>
                            <span class="ds-version-card__specs-value tw-font-semibold lg:tw-font-normal">
                                {{ item.traction }}
                            </span>
                        </div>
                        <div class="ds-version-card__price-values tw-basis-[13%] tw-whitespace-nowrap">
                            <dk-badge-dst type="light-blue" size="small" class="tw-mb-3">
                                <span>{{ displayOffer(item).label }}</span>
                                <dk-icon name="ds-tag-price-filled" size="xs" class="tw-ml-2" />
                            </dk-badge-dst>
                            <h4
                                class="ds-version-card__price-main tw-font-semibold lg:tw-font-bold lg:tw-text-lg tw-text-black"
                            >
                                {{ displayOffer(item).finalPrice || displayOffer(item).originalPrice }}
                            </h4>
                            <del
                                v-if="
                                    displayOffer(item).finalPrice &&
                                        displayOffer(item).originalPrice &&
                                        displayOffer(item).finalPrice !== displayOffer(item).originalPrice
                                "
                                class="ds-version-card__price-old"
                            >
                                {{ displayOffer(item).originalPrice }}
                            </del>
                        </div>
                        <div class="ds-version-card__cta tw-flex tw-flex-col tw-ml-auto">
                            <dk-button
                                class="ds-version-card__cta-button"
                                type="button"
                                @click="onSelect(item.pricing, item.name, item.id)"
                            >
                                {{ $t('model.version.select') }}
                            </dk-button>

                            <dk-button
                                v-if="item.pricing.length > 1"
                                variant="ghost"
                                role="button"
                                class="ds-version-card__offers-link tw-mt-4 tw-text-base tw-leading-sm"
                                @click="handleOfferLinkClick(item.pricing, item.name, item.id)"
                            >
                                {{ $tc('model.version.other-offers', item.pricing.length, { n: item.pricing.length }) }}
                            </dk-button>
                        </div>
                    </div>
                    <div
                        v-if="hasLegalCompliances(item)"
                        class="ds-version-card__legal-compliances tw-text-2xs lg:tw-text-xs tw-text-base-subtle"
                    >
                        <dk-e-label
                            v-if="efficiencyClass(item)"
                            class="lg:tw-hidden tw-mr-4"
                            :e-label="efficiencyClass(item).value"
                            :width-auto="true"
                            size="small"
                        >
                            {{ efficiencyClass(item).value }}
                        </dk-e-label>
                        <dk-e-label
                            v-if="efficiencyClass(item)"
                            class="tw-hidden lg:tw-inline-flex tw-mr-6"
                            :e-label="efficiencyClass(item).value"
                            :width-auto="true"
                        >
                            {{ efficiencyClass(item).value }}
                        </dk-e-label>
                        <span v-if="co2(item)" class="ds-version-card__legal-compliances-item">
                            {{ co2(item).label }}&nbsp;{{ co2(item).value }}
                        </span>
                        <span v-if="combinedConsumption(item)" class="ds-version-card__legal-compliances-item">
                            {{ combinedConsumption(item).label }}&nbsp;{{ combinedConsumption(item).value }}
                        </span>
                        <span v-if="combinedMethaneConsumption(item)" class="ds-version-card__legal-compliances-item">
                            {{ combinedMethaneConsumption(item).label }}&nbsp;{{
                                combinedMethaneConsumption(item).value
                            }}
                        </span>
                        <span v-if="combinedElectricConsumption(item)" class="ds-version-card__legal-compliances-item">
                            {{ combinedElectricConsumption(item).label }}&nbsp;{{
                                combinedElectricConsumption(item).value
                            }}
                        </span>
                    </div>
                </div>
                <hr
                    v-if="index < version.items.length - 1"
                    class="ds-version-card__divider -tw-mx-4 tw-my-4 lg:-tw-mx-6 lg:tw-my-6 tw-border-t tw-border-solid tw-border-border-color"
                >
            </div>
        </dk-accordion>
    </div>
</template>
<script>
import DkAccordion from '@v4/common/components/molecules/DkAccordionDST.vue';
import DkButton from '@v4/common/components/atoms/DkButton.vue';
import DkELabel from '@v4/common/components/atoms/DkELabel.vue';
import DkBadgeDst from '@v4/common/components/atoms/DkBadgeDST.vue';
import DkIcon from '@v4/common/components/atoms/DkIcon.vue';

import { modalVersionsOfferSelection, modalEmissionConsumption } from '@v4/common/components/mixins/modal-service';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { getQueryParams } from '@v4/common/utils/url';

/**
 * A card to display vehicle versions
 *
 * @version 4.0.0
 * @example ./examples/DsVersionCard.md
 */
export default {
    name: 'DsVersionCard',
    components: {
        DkAccordion,
        DkButton,
        DkELabel,
        DkBadgeDst,
        DkIcon,
    },
    mixins: [dkModalAware, modalVersionsOfferSelection, modalEmissionConsumption, gaEventHandler],
    props: {
        /**
         * Vehicle version info object.
         */
        version: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Selected offer item object.
         */
        selectedOffer: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Whether or not the card is expanded.
         */
        isExpanded: {
            type: Boolean,
            default: false,
        },
        /**
         * Set version item as selected.
         */
        setSelectedOffer: {
            type: Function,
            default: () => {},
        },
    },
    computed: {
        versionTitle() {
            const allowedLocales = ['it', 'es'];

            if (allowedLocales.includes(this.$i18n.locale)) {
                return `${this.$t('ds-version-card.price-list')} ${this.version.name}`;
            }

            return this.version.name;
        },
        versionsNumber() {
            return `(${this.$tc('ds-version-card.version-count', this.version.items.length, {
                n: this.version.items.length,
            })})`;
        },
        hasWizardForm() {
            return !!getQueryParams().feature_toggle_form_wizard;
        },
    },
    methods: {
        displayOffer(item) {
            return item.pricing[0];
        },
        handleOfferLinkClick(pricing, versionName, versionId) {
            const offers = pricing;
            offers.forEach((offer) => {
                if (offer.versionId === this.selectedOffer.versionId && offer.offerId === this.selectedOffer.offerId) {
                    offer.selected = true;
                }
            });

            const version = this.getVersionData(pricing, versionName, versionId, true);

            if (this.hasWizardForm) {
                this.openWizardFormModal(version);
            } else {
                this.openVersionsOfferSelectionModal(version, this.setSelectedOffer);
            }
        },
        onSelect(pricing, versionName, versionId) {
            this.onSelectVersion(versionId);

            if (pricing.length > 1) {
                const version = this.getVersionData(pricing, versionName, versionId);

                if (this.selectedOffer.versionId === versionId) {
                    this.setSelectedOffer(this.selectedOffer, versionName);
                } else if (this.hasWizardForm) {
                    this.openWizardFormModal(version);
                } else {
                    this.openVersionsOfferSelectionModal(version, this.setSelectedOffer);
                }
            } else {
                const offer = pricing[0];
                offer.versionId = versionId;
                this.setSelectedOffer(offer, versionName);

                if (this.hasWizardForm) {
                    const version = this.getVersionData(pricing, versionName, versionId);
                    this.openWizardFormModal(version);
                }
            }
        },
        onSelectVersion(versionId) {
            this.$emit('click-select-version', versionId);
        },
        hasLegalCompliances(item) {
            return (
                item.detailsEmissionConsumption &&
                (item.detailsEmissionConsumption.co2 ||
                    item.detailsEmissionConsumption.combinedConsumption ||
                    item.detailsEmissionConsumption.combinedMethaneConsumption ||
                    item.detailsEmissionConsumption.efficiencyClass)
            );
        },
        combinedConsumption(item) {
            return item.detailsEmissionConsumption && item.detailsEmissionConsumption.combinedConsumption;
        },
        combinedMethaneConsumption(item) {
            return item.detailsEmissionConsumption && item.detailsEmissionConsumption.combinedMethaneConsumption;
        },
        combinedElectricConsumption(item) {
            return item.detailsEmissionConsumption && item.detailsEmissionConsumption.combinedElectricConsumption;
        },
        co2(item) {
            return item.detailsEmissionConsumption && item.detailsEmissionConsumption.co2;
        },
        efficiencyClass(item) {
            return item.detailsEmissionConsumption && item.detailsEmissionConsumption.efficiencyClass;
        },
        handleEfficiencyTextClick(item) {
            this.openEmissionConsumptionModal(item.name, item.detailsEmissionConsumption);
        },
        openWizardFormModal(version) {
            this.$emit('click-open-wizard-form-modal', version);
        },
        getVersionData(pricing, name, id, getAllOffers = false) {
            const addAllOffers = !this.hasWizardForm || (this.hasWizardForm && getAllOffers);
            const addFirstOffer = this.hasWizardForm && pricing.length && !getAllOffers;

            return {
                name,
                id,
                ...(addAllOffers && { offers: pricing }),
                ...(addFirstOffer && { offer: pricing[0] }),
            };
        },
    },
};
</script>
